/**
 * system_functions.func_type 前端對應常數（對應後端 app/core/func_type.py）
 *
 * **設計原則**
 *
 * CE（社群版）硬編兩種類型：NODE (1) / PAGE (2)，有對應的 i18n key。
 * EE 商業模組（API 服務、排程任務、...）由後端 `register_func_type()` 動態註冊，
 * 前端呼叫 `loadFuncTypes()` 從 `/api/system/func-types` 同步進 FUNC_TYPE_META。
 *
 * 載入後 `FUNC_TYPE_META` 是本 helper 的單一真實來源，所有頁面統一透過
 * `getAllFuncTypes()` / `getFuncTypeLabel()` / `isLeafType()` / ... 存取。
 */

import axios from '../api/axios';

export const FuncType = {
  NODE: 1,
  PAGE: 2,
} as const;

export type FuncTypeValue = typeof FuncType[keyof typeof FuncType];

export interface FuncTypeMeta {
  name_zh_tw: string;
  shows_in_menu: boolean;
  is_leaf: boolean;
  requires_module_code: boolean;
  edition: string; // 'ce' | 'ee_api_service' | 'ee_scheduler' | ...
}

// CE 保留類型（同步預填，避免首次渲染沒資料）
export const FUNC_TYPE_META: Record<number, FuncTypeMeta> = {
  [FuncType.NODE]: {
    name_zh_tw: '節點',
    shows_in_menu: true,
    is_leaf: false,
    requires_module_code: false,
    edition: 'ce',
  },
  [FuncType.PAGE]: {
    name_zh_tw: '功能頁',
    shows_in_menu: true,
    is_leaf: true,
    requires_module_code: true,
    edition: 'ce',
  },
};

// CE 類型 i18n key 對應（EE 類型直接用 name_zh_tw，不走 i18n）
const CE_I18N_KEYS: Record<number, string> = {
  [FuncType.NODE]: 'sysFunctions.types.node',
  [FuncType.PAGE]: 'sysFunctions.types.function',
};

let _loaded = false;

/**
 * 從後端同步所有已註冊的 func_type（含 EE 模組動態註冊）。
 * 冪等，第二次以後直接 return，避免多頁重複呼叫。
 */
export async function loadFuncTypes(): Promise<void> {
  if (_loaded) return;
  try {
    const res = await axios.get('/api/system/func-types');
    for (const t of res.data as Array<{ value: number } & FuncTypeMeta>) {
      FUNC_TYPE_META[t.value] = {
        name_zh_tw: t.name_zh_tw,
        shows_in_menu: t.shows_in_menu,
        is_leaf: t.is_leaf,
        requires_module_code: t.requires_module_code,
        edition: t.edition,
      };
    }
    _loaded = true;
  } catch (err) {
    console.error('[funcType] loadFuncTypes failed:', err);
  }
}

/** 取得顯示標籤：CE 類型優先用 i18n；其餘用 name_zh_tw */
export function getFuncTypeLabel(
  value: number,
  t?: (key: string) => string,
): string {
  const i18nKey = CE_I18N_KEYS[value];
  if (i18nKey && t) {
    const translated = t(i18nKey);
    if (translated && translated !== i18nKey) return translated;
  }
  return FUNC_TYPE_META[value]?.name_zh_tw || `#${value}`;
}

/** 取得所有已知 func_type（含 CE + 已載入的 EE），依 value 排序 */
export function getAllFuncTypes(): Array<{ value: number; meta: FuncTypeMeta }> {
  return Object.entries(FUNC_TYPE_META)
    .map(([v, meta]) => ({ value: Number(v), meta }))
    .sort((a, b) => a.value - b.value);
}

export const isMenuType = (t: number): boolean =>
  FUNC_TYPE_META[t]?.shows_in_menu ?? false;

export const isLeafType = (t: number): boolean =>
  FUNC_TYPE_META[t]?.is_leaf ?? false;

export const isNodeType = (t: number): boolean =>
  t === FuncType.NODE;
